import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import Grid from '@material-ui/core/Grid'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import LinearProgress from '@mui/material/LinearProgress'
import ButtonInc from '../../../../shared-ui/ButtonInc'
import {
  RecentPoliciesHeader,
  DisplayRecentPolicies
} from '../../../../components/RecentPolicies'
import { getRecentPoliciesRequest } from '../../../../components/RecentPolicies/Redux/actions'
import PageNotFound from '../../../../containers/PageNotFound/index.es6'
import { PARTNERS_GUID_ENUM } from '../../../../routes/PartnersEnum'
import { getPartnerGuid, hasAuthourity } from '../../../../lib/access.es6'
import { getLookupList } from '../../../../lib/requests.es6'

// Free-text worksite filter, applied server-side as an exact
// additional_details.worksite_code match (policy-administration-complex's
// /policies/complex/filter does exact, case-insensitive matches per key —
// see find.policies.dto.ts). A searchable Autocomplete rather than a plain
// text field since there are ~1,611 codes and ops staff know the worksite
// name, not the code. Worksites are OMM-managed in Lookup Settings;
// inactive ones stay listed since older policies still carry them.
export function OmmWorksiteFilter(props) {
  const [worksites, setWorksites] = useState([])

  useEffect(function() {
    getLookupList('worksites')
      .then(function(response) {
        setWorksites(
          (response.data || []).map(function(item) {
            return { worksite_code: item.value, worksite_name: item.name }
          })
        )
      })
      .catch(function() {
        setWorksites([])
      })
  }, [])

  return (
    <Autocomplete
      size="small"
      options={worksites}
      getOptionLabel={worksite =>
        worksite ? `${worksite.worksite_name} (${worksite.worksite_code})` : ''
      }
      isOptionEqualToValue={(option, value) =>
        option.worksite_code === value.worksite_code
      }
      value={props.value}
      onChange={function(event, worksite) {
        props.onChange(worksite)
      }}
      disabled={props.disabled}
      sx={{ flex: 1, minWidth: 260 }}
      renderInput={params => (
        <TextField
          {...params}
          label="Filter by Worksite"
          placeholder="Search worksites..."
        />
      )}
    />
  )
}

const OMM_DATE_DISCRIMINATORS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'start_date', label: 'Start Date' },
  { value: 'due_date', label: 'Due Date' }
]

function OmmDateFilters(props) {
  const defaultDateBy = props.defaultDateBy || 'created_at'
  const [inputs, setInputs] = useState({
    dateBy: defaultDateBy,
    fromDate: '',
    toDate: ''
  })

  useEffect(
    function() {
      setInputs({
        dateBy: props.defaultDateBy || 'created_at',
        fromDate: '',
        toDate: ''
      })
    },
    [props.tabKey, props.defaultDateBy]
  )

  const setField = function(field) {
    return function(e) {
      const val = e.target.value
      setInputs(function(prev) {
        return { ...prev, [field]: val }
      })
    }
  }

  const invalidRange =
    !!inputs.fromDate && !!inputs.toDate && inputs.fromDate > inputs.toDate

  const handleApply = function() {
    const params = {}
    if (inputs.fromDate) params.from = `${inputs.fromDate}T00:00:00.000Z`
    if (inputs.toDate) params.to = `${inputs.toDate}T23:59:59.999Z`
    if (inputs.fromDate || inputs.toDate)
      params.date_discriminator = inputs.dateBy
    props.onApply(params)
  }

  const handleClear = function() {
    setInputs({ dateBy: defaultDateBy, fromDate: '', toDate: '' })
    props.onApply({})
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        alignItems: 'flex-start',
        mb: 2
      }}
    >
      <TextField
        select
        size="small"
        label="Filter Date By"
        value={inputs.dateBy}
        onChange={setField('dateBy')}
        sx={{ flex: 1, minWidth: 140 }}
      >
        {OMM_DATE_DISCRIMINATORS.map(function(opt) {
          return (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          )
        })}
      </TextField>
      <TextField
        size="small"
        type="date"
        label="From Date"
        value={inputs.fromDate}
        onChange={setField('fromDate')}
        error={invalidRange}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: 1, minWidth: 140 }}
      />
      <TextField
        size="small"
        type="date"
        label="To Date"
        value={inputs.toDate}
        onChange={setField('toDate')}
        error={invalidRange}
        helperText={invalidRange ? 'From must be on or before To' : ''}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: 1, minWidth: 140 }}
      />
      <ButtonInc
        variant="contained"
        onClick={handleApply}
        disabled={invalidRange || props.disabled}
        sx={{ textTransform: 'none' }}
      >
        Apply Filters
      </ButtonInc>
      <ButtonInc
        variant="outlined"
        onClick={handleClear}
        disabled={props.disabled}
        sx={{ textTransform: 'none' }}
      >
        Clear
      </ButtonInc>
    </Box>
  )
}

// Tab: { key, label, params, filter, authorities, emptyMessage, showAmlFlag,
//        showStage, dateDiscriminator }
//
// `authorities` is an OR: the tab shows when the user holds any of them, and a
// tab without them shows to everyone who can reach the page. A desk is reachable
// only while it has a tab to show, so a sales agent typing another desk's URL
// gets PageNotFound rather than an empty dashboard.
// A tab with a filter must also pass fetchAll, since the server's page and
// total then describe the unnarrowed set.

class DeskDashboard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      tabValue: 0,
      searchTerm: '',
      dateFilter: {},
      worksiteFilter: null
    }
  }

  isPermitted() {
    return (
      getPartnerGuid() === PARTNERS_GUID_ENUM.OLDMUTUAL_MALAWI &&
      this.tabs().length > 0
    )
  }

  tabs() {
    return this.props.tabs.filter(function(tab) {
      if (!tab.authorities) {
        return true
      }
      return tab.authorities.some(function(authority) {
        return hasAuthourity(authority)
      })
    })
  }

  currentTab() {
    const tabs = this.tabs()
    return tabs[this.state.tabValue] || tabs[0]
  }

  // Tabs that differ only in their client-side filter ask the server the same
  // question — Un Confirmed and Need Action are both the unconfirmed queue over
  // the same dates — so switching between them would otherwise re-sweep the
  // whole bucket to arrive at the set already in the store. A failed load is
  // always retried: there is nothing loaded to reuse.
  fetch(tab, payload) {
    const request = {
      page: 1,
      size: 10,
      ...tab.params,
      ...this.state.dateFilter,
      ...(this.state.searchTerm
        ? { policy_number: this.state.searchTerm }
        : {}),
      ...payload
    }

    // additional_details merges rather than replaces: tab.params may already
    // carry agent_branch (branch-scoped sales agents/managers, see
    // buildOmmTabParams) — losing that here would show them other branches'
    // policies, not just add a worksite narrowing on top of their own.
    if (this.state.worksiteFilter) {
      request.additional_details = {
        ...(request.additional_details || {}),
        worksite_code: this.state.worksiteFilter.worksite_code
      }
    }

    const signature = JSON.stringify(request)
    const failed = !!(this.props.recentPoliciesData || {}).errors
    if (signature === this.lastQuery && !failed) {
      return
    }
    this.lastQuery = signature

    this.props.dispatch(getRecentPoliciesRequest(request))
  }

  componentWillMount() {
    if (!this.isPermitted()) {
      return
    }
    this.fetch(this.tabs()[0])
  }

  handleTabChange = (event, tabValue) => {
    this.setState(
      { tabValue, searchTerm: '', dateFilter: {}, worksiteFilter: null },
      () => {
        this.fetch(this.tabs()[tabValue])
      }
    )
  }

  handleDateFilterApply = dateParams => {
    this.setState({ dateFilter: dateParams }, () => {
      this.fetch(this.currentTab(), { page: 1, size: 10 })
    })
  }

  handleWorksiteFilterChange = worksite => {
    this.setState({ worksiteFilter: worksite }, () => {
      this.fetch(this.currentTab(), { page: 1, size: 10 })
    })
  }

  handleSearch = value => {
    this.setState({ searchTerm: value }, () => {
      this.fetch(this.currentTab(), { page: 1, size: 10 })
    })
  }

  handleNextPage = (payload = {}) => {
    this.fetch(this.currentTab(), payload)
  }

  render() {
    if (!this.isPermitted()) {
      return <PageNotFound />
    }

    const tabs = this.tabs()
    const tab = this.currentTab()
    const { recentPoliciesData } = this.props
    const { policies = [], loader = false, page = 1, size = 10, total = 0 } =
      recentPoliciesData || {}

    return (
      <>
        <RecentPoliciesHeader title={this.props.title} />

        <section style={{ padding: '20px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              {tabs.length > 1 ? (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={this.state.tabValue}
                    onChange={this.handleTabChange}
                    aria-label={`${this.props.title} tabs`}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {tabs.map(entry => (
                      <Tab key={entry.key} label={entry.label} />
                    ))}
                  </Tabs>
                </Box>
              ) : null}

              <OmmDateFilters
                tabKey={tab.key}
                defaultDateBy={tab.dateDiscriminator || 'created_at'}
                onApply={this.handleDateFilterApply}
                disabled={loader}
              />

              <Box sx={{ mb: 2 }}>
                <OmmWorksiteFilter
                  value={this.state.worksiteFilter}
                  onChange={this.handleWorksiteFilterChange}
                  disabled={loader}
                />
              </Box>

              {loader ? <LinearProgress sx={{ mb: 1 }} /> : null}

              <DisplayRecentPolicies
                key={tab.key}
                policies={tab.filter ? policies.filter(tab.filter) : policies}
                handleNextPage={this.handleNextPage}
                loader={loader}
                page={page}
                size={size}
                total={total}
                emptyMessage={tab.emptyMessage}
                showAmlFlag={!!tab.showAmlFlag}
                showStage={!!tab.showStage}
                needAction={tab.needAction}
                makerStage={tab.makerStage}
                localPaging={!!tab.filter}
                searchValue={this.state.searchTerm}
                onSearch={this.handleSearch}
              />
            </Grid>
          </Grid>
        </section>
      </>
    )
  }
}

export default connect(state => ({
  recentPoliciesData: state.recentPoliciesData
}))(DeskDashboard)
